import {
  Button,
  Color,
  Font,
  Icon,
  Spacing,
  Table,
  TableCol,
  TablePagination,
  TableProps,
  TableSort,
  Text,
} from 'zens-storybook'
import { useState } from 'react'
import { ActivityIndicator, StyleSheet, View } from 'react-native'
import { Trans, useTranslation } from 'react-i18next'
import {
  productConnectivityStates,
  productOccupancyStates,
  productPeripheralStates,
  titleForProductConnectivityState,
  titleForProductOccupancyState,
  titleForProductPeripheralState,
  useStore,
  useCompany,
  useBuildings,
  useFloors,
  Workspace,
  WorkspacesFilter,
  useLocations,
  useWorkspaceTypes,
  useWorkspaces,
  useFetchWorkspaces,
} from 'zens-store'
import { Href, Link } from 'zens-router'
import * as XLSX from 'xlsx'
import { ProductConnectivity, ProductOccupancy, ProductPeripherals } from 'zens-fragment'

import { TablePaginated } from '../table'
import { MenuMore } from '../menu'

import WorkspaceLocationCell from './locationcell'
import WorkspaceEmpty from './empty'
import WorkspaceFilter from './filter'

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  head: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    marginBottom: Spacing.S,
  },
  button: {
    marginLeft: Spacing.XS,
  },
  buttonInactive: {
    borderColor: Color.calmGrey,
  },
  iconInactive: {
    color: Color.metalGrey,
  },
  location: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  top: {
    flexDirection: 'row',
    justifyContent: 'center',
    zIndex: 10,
  },
  filter: {
    flex: 1,
  },
  download: {
    marginHorizontal: 10,
    height: 38,
  },
  moreMenu: {
    zIndex: 20,
  },
  more: {
    backgroundColor: Color.simplyWhite,
    borderBottomColor: Color.calmGrey,
    borderBottomWidth: 1,
  },
  moreTitle: {
    fontFamily: Font.medium,
  },
  skeletonButton: {
    minWidth: 0,
  },
  skeletonMore: {
    width: 40,
    alignItems: 'center',
    height: 38,
    justifyContent: 'center',
  },
  help: {
    flex: 1,
  },
  helpText: {
    color: Color.simplyWhite,
  },
  helpTitle: {
    width: 170,
  },
})

export interface Props extends Omit<TableProps, 'rows' | 'cols'> {
  companyId: number
  zoneId?: number
  pagination?: TablePagination
  onPaginationChange?: (pagination: TablePagination) => void
  sort?: TableSort
  onSortChange?: (sort: TableSort) => void
  filter?: WorkspacesFilter
  onFilterChange?: (filter: WorkspacesFilter) => void
  onRemove?: (workspace: Workspace) => void
  addHref?: Href
  selectHref?: (workspaceId: number) => Href
  typeHref?: (type: 'table' | 'floorplan') => Href
}

const WorkspaceTable = (props: Props) => {
  const {
    companyId,
    zoneId,
    pagination = { page: 0, size: 10 },
    onPaginationChange,
    sort = { field: 'name', direction: 'asc' },
    onSortChange,
    filter,
    onFilterChange,
    onRemove,
    addHref,
    selectHref,
    typeHref,
    ...otherProps
  } = props
  const { t } = useTranslation()
  const { message } = useStore()
  const [downloading, setDownloading] = useState(false)

  const hasFilter = Object.keys(filter || {}).length > 0

  const company = useCompany({ companyId })
  const buildings = useBuildings({ companyId })
  const floors = useFloors({ companyId })
  const locations = useLocations({ companyId })
  const workspaceTypes = useWorkspaceTypes()
  const workspaces = useWorkspaces({ companyId, pagination, sort, filter })
  const fetchWorkspaces = useFetchWorkspaces()

  const types = useWorkspaceTypes()
  const total = workspaces.data?.meta?.total || 0

  const rows =
    workspaces.data?.data?.map((workspace) => ({
      data: workspace,
      href: selectHref?.(workspace.id),
    })) || []

  async function download() {
    try {
      setDownloading(true)
      const data = await fetchWorkspaces({ companyId, sort, filter })
      const workspaceTypeData = filter?.workspaceTypeId
        ?.map((workspaceTypeId) => workspaceTypes.data?.find((workspaceType) => workspaceType?.id === workspaceTypeId))
        .sort((a, b) => a?.name?.localeCompare(b?.name || '') || 0)

      const floorData = filter?.floorId
        ?.map((floorId) => floors.data?.find((floor) => floor?.id === floorId))
        .map((floor) => {
          const building = buildings.data?.find((building) => building?.id === floor?.buildingId)
          return { floor, building }
        })
        .sort(
          (a, b) =>
            a?.building?.name?.localeCompare(b?.building?.name || '') ||
            (a?.floor?.number || 0) - (b?.floor?.number || 0),
        )

      const occupancyStateData = filter?.occupancyState?.sort(
        (a, b) => productOccupancyStates.indexOf(a) - productOccupancyStates.indexOf(b),
      )

      const connectivityStateData = filter?.connectivityState?.sort(
        (a, b) => productConnectivityStates.indexOf(a) - productConnectivityStates.indexOf(b),
      )

      const peripheralsStateData = filter?.peripheralsState?.sort(
        (a, b) => productPeripheralStates.indexOf(a) - productPeripheralStates.indexOf(b),
      )

      const rows: (string | number | Date)[][] = []
      rows.push([t('Company'), company.data?.name || '-'])
      if (workspaceTypeData?.length)
        rows.push([
          t('Workspace type', { count: workspaceTypeData.length }),
          workspaceTypeData?.map((item) => item?.name || '-').join(', '),
        ])
      if (floorData?.length)
        rows.push([
          t('Floor', { count: floorData.length }),
          floorData?.map(({ floor, building }) => `${building?.name}: ${floor?.displayName || '-'}`).join(', '),
        ])
      if (filter?.isInstalled !== undefined)
        rows.push([t('Installed'), filter?.isInstalled ? t('Installed') : t('Not installed')])

      if (occupancyStateData?.length)
        rows.push([
          t('Occupancy'),
          occupancyStateData?.map((state) => titleForProductOccupancyState(state, t)).join(', '),
        ])
      if (connectivityStateData?.length)
        rows.push([
          t('Connectivity'),
          connectivityStateData?.map((state) => titleForProductConnectivityState(state, t)).join(', '),
        ])
      if (peripheralsStateData?.length)
        rows.push([
          t('Peripherals'),
          peripheralsStateData?.map((state) => titleForProductPeripheralState(state, t)).join(', '),
        ])

      rows.push([])
      rows.push([t('Name'), t('Workspace type'), t('Location'), t('Occupancy'), t('Connectivity'), t('Peripherals')])

      data?.data?.forEach((workspace) => {
        const type = types.data?.find((item) => item?.id === workspace.workspaceTypeId)
        const floor = floors.data?.find((item) => item?.id === workspace.floorId)
        const building = buildings.data?.find((item) => item?.id === floor?.buildingId)
        const location = locations.data?.find((item) => item?.id === building?.locationId)
        rows.push([
          workspace.name || '-',
          type?.name || '-',
          `${floor?.displayName} (${building?.name}), ${location?.name}`,
          titleForProductOccupancyState(workspace.occupancyState, t),
          titleForProductConnectivityState(workspace.connectivityState, t),
          titleForProductPeripheralState(workspace.peripheralsState, t),
        ])
      })

      const worksheet = XLSX.utils.aoa_to_sheet(rows)
      const workbook = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Data')
      XLSX.writeFile(workbook, `${t('Workspaces')}.xlsx`, { compression: true })
      setDownloading(false)
    } catch (error) {
      message.add({ message: t('Error generating data'), status: 'error' })
      console.error('Error generating data', error)
      setDownloading(false)
    }
  }

  const renderType = (workspace: Workspace) => {
    const { workspaceTypeId } = workspace
    const type = types.data?.find((item) => item.id === workspaceTypeId)
    return <Text>{type?.name}</Text>
  }

  const renderLocation = (workspace: Workspace) => {
    return <WorkspaceLocationCell companyId={companyId} workspace={workspace} />
  }

  const renderConnectivityState = (workspace: Workspace) => {
    const { connectivityState } = workspace
    return (
      <View style={styles.location}>
        <ProductConnectivity value={connectivityState} />
      </View>
    )
  }

  const renderOccupancyState = (workspace: Workspace) => {
    const { occupancyState } = workspace
    return (
      <View style={styles.location}>
        <ProductOccupancy value={occupancyState} />
      </View>
    )
  }

  const renderPeripheralsState = (workspace: Workspace) => {
    const { peripheralsState } = workspace
    return (
      <View style={styles.location}>
        <ProductPeripherals value={peripheralsState} />
      </View>
    )
  }

  const renderRemove = (workspace: Workspace) => {
    return (
      <View style={styles.location}>
        <Button left="minus" light round onPress={() => onRemove?.(workspace)} testID="WorkspaceTable.Remove" />
      </View>
    )
  }

  const cols: TableCol<Workspace>[] = [
    {
      key: 'name',
      title: t('Workspace name'),
      help: t('The workspace names that are located in this company '),
    },
    {
      key: 'workspace_type',
      title: t('Workspace type'),
      render: renderType,
      help: t('The type of the workspace'),
    },
    {
      key: 'floor',
      title: t('Location'),
      render: renderLocation,
      help: t('The location of the workspace'),
    },
    {
      key: 'occupancy_state',
      title: t('Occupancy'),
      render: renderOccupancyState,
      help: t(
        'Workspace occupancy indicates whether a workspace is available or in use. An unknown status means the device is either offline or has reported a sensor malfunction or disconnected sensor.',
      ),
      size: {
        width: 150,
      },
    },
    {
      key: 'connectivity_state',
      title: t('Connectivity'),
      render: renderConnectivityState,
      help: (
        <View style={styles.help}>
          <Text style={styles.helpText}>
            <Trans>Workspace connectivity status definitions</Trans>:
          </Text>
          <Text style={styles.helpText}>
            <View style={styles.helpTitle}>
              <Trans>Online</Trans>
            </View>
            <Trans>The device is connected and functioning correctly.</Trans>
          </Text>
          <Text style={styles.helpText}>
            <View style={styles.helpTitle}>
              <Trans>Connection lost</Trans>
            </View>
            <Trans>The device has been disconnected for at least 2 hours.</Trans>
          </Text>
          <Text style={styles.helpText}>
            <View style={styles.helpTitle}>
              <Trans>Offline</Trans>
            </View>
            <Trans>The device has been non-functional for 24 hours or more.</Trans>
          </Text>
          <Text style={styles.helpText}>
            <View style={styles.helpTitle}>
              <Trans>Never online</Trans>
            </View>
            <Trans>The device has never established a connection.</Trans>
          </Text>
        </View>
      ),
      size: {
        width: 150,
      },
    },
    {
      key: 'peripherals_state',
      title: t('Peripherals'),
      render: renderPeripheralsState,
      help: t(
        'Indicates whether all workspace peripherals are connected and functioning as expected. If a device is not online, this status will be reported as unknown.',
      ),
      size: {
        width: 150,
      },
    },
    ...(onRemove
      ? [
          {
            key: 'action',
            title: ' ',
            sort: false as const,
            render: renderRemove,
            size: {
              width: 40,
            },
          },
        ]
      : []),
  ]
  return (
    <View style={styles.container}>
      <View style={styles.head}>
        <Link href={typeHref?.('table') || ''} asChild>
          <Button left={<Icon name="menu" size={18} />} style={styles.button} light testID="Table.TableButton" />
        </Link>
        <Link href={typeHref?.('floorplan') || ''} asChild>
          <Button
            left={<Icon name="map" size={18} style={styles.iconInactive} />}
            style={styles.button}
            light
            contentContainerStyle={styles.buttonInactive}
            testID="Table.FloorplanButton"
          />
        </Link>
      </View>
      <View style={styles.top}>
        {!zoneId && (
          <>
            <WorkspaceFilter
              companyId={companyId}
              filter={filter}
              onFilterChange={onFilterChange}
              loading={workspaces.isFetching}
              style={styles.filter}
            />
            {downloading ? (
              <ActivityIndicator
                size={20}
                style={styles.download}
                color={Color.premiumBlack}
                testID="WorkspaceTable.Spinner"
              />
            ) : (
              <MenuMore
                items={[
                  {
                    key: 'title',
                    title: t('Download options'),
                    disabled: true,
                    style: styles.more,
                    textStyle: styles.moreTitle,
                  },

                  {
                    key: 'data',
                    icon: 'download',
                    title: t('Download data'),
                    testID: 'WorkspaceTable.Download',
                  },
                ]}
                onSelect={download}
                testID="WorkspaceTable.More"
                style={styles.moreMenu}
              />
            )}
          </>
        )}
      </View>
      <TablePaginated
        rows={rows}
        cols={cols}
        pagination={pagination}
        count={total}
        renderCount={() =>
          t('{{page}} of {{numberOfPages}}', {
            page: pagination.page + 1,
            numberOfPages: Math.ceil(total / pagination.size),
          })
        }
        sizeLabel={t('Workspaces per page')}
        onPaginationChange={onPaginationChange}
        sort={sort}
        onSortChange={onSortChange}
        dataIsSorted
        ListEmptyComponent={<WorkspaceEmpty search={filter?.search} hasFilter={hasFilter} href={addHref} />}
        {...otherProps}
      />
    </View>
  )
}

interface SkeletonProps {
  filter?: boolean
  zoneId?: number
}

const Skeleton = (props: SkeletonProps) => {
  const { filter = false, zoneId } = props
  const { t } = useTranslation()
  const cols: TableCol<Workspace>[] = [
    {
      key: 'name',
      title: t('Workspace name'),
    },
    {
      key: 'workspace_type',
      title: t('Workspace type'),
    },
    {
      key: 'floor',
      title: t('Location'),
    },
    {
      key: 'occupancy_state',
      title: t('Occupancy'),
      size: {
        width: 150,
      },
    },
    {
      key: 'connectivity_state',
      title: t('Connectivity'),
      size: {
        width: 150,
      },
    },
    {
      key: 'peripherals_state',
      title: t('Peripherals'),
      size: {
        width: 150,
      },
    },
  ]
  return (
    <View style={styles.container}>
      <View style={styles.head}>
        <Button.Skeleton style={styles.button} contentStyle={styles.skeletonButton} />
        <Button.Skeleton style={styles.button} contentStyle={styles.skeletonButton} />
      </View>
      <View style={styles.top}>
        {!zoneId && (
          <>
            {filter && <WorkspaceFilter.Skeleton style={styles.filter} />}
            <View style={styles.skeletonMore}>
              <Icon name="dots" color={Color.calmGrey} />
            </View>
          </>
        )}
      </View>
      <Table.Skeleton cols={cols} />
    </View>
  )
}
WorkspaceTable.Skeleton = Skeleton
export default WorkspaceTable
